import React, { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function TablePagination({
  table,
  currentPage: propCurrentPage,
  totalPages: propTotalPages,
  pageSize: propPageSize,
  totalItems: propTotalItems,
  totalRows: propTotalRows,
  pageCount: propPageCount,
  itemLabel = "products",
  onPageChange: propOnPageChange,
  onPageSizeChange: propOnPageSizeChange,
  pageSizeOptions = [10, 15, 25, 50, 100],
  showPageSize = true,
  showQuickJumper = true,
  showPageNumbers = true,
  siblingCount = 1,
  footerInfo,
  isSelectionEnabled,
  selectedRowsCount: propSelectedRowsCount,
  filteredRowsCount: propFilteredRowsCount,
  className,
}) {
  const [jumpInput, setJumpInput] = useState("");

  // Determine current page (1-based)
  const current =
    propCurrentPage ??
    (table ? table.getState().pagination.pageIndex + 1 : 1);

  // Determine page size
  const size =
    propPageSize ?? (table ? table.getState().pagination.pageSize : 15);

  // Determine total pages
  let total = propTotalPages ?? propPageCount;
  if (total === undefined && table) {
    const tablePageCount = table.getPageCount();
    total = tablePageCount > 0 ? tablePageCount : -1;
  }
  const isTotalPagesKnown = total !== undefined && total > 0;
  const safeTotalPages = isTotalPagesKnown ? Math.max(1, total) : 1;

  // Determine total items count if available
  let totalItems = propTotalItems ?? propTotalRows;
  if (totalItems === undefined && table && !table.options.manualPagination) {
    totalItems = table.getFilteredRowModel().rows.length;
  }

  // Row selection counts
  const selectedCount =
    propSelectedRowsCount ??
    (table && isSelectionEnabled
      ? table.getFilteredSelectedRowModel().rows.length
      : 0);
  const filteredCount =
    propFilteredRowsCount ??
    (table && isSelectionEnabled
      ? table.getFilteredRowModel().rows.length
      : 0);

  // Previous & Next state
  const canPrevious =
    table && !propCurrentPage
      ? table.getCanPreviousPage()
      : current > 1;
  const canNext =
    table && !propCurrentPage
      ? table.getCanNextPage()
      : isTotalPagesKnown
        ? current < safeTotalPages
        : true;

  // Page change handler
  const handlePageChange = (newPage) => {
    const targetPage = isTotalPagesKnown
      ? Math.max(1, Math.min(newPage, safeTotalPages))
      : Math.max(1, newPage);

    if (targetPage === current) return;

    if (propOnPageChange) {
      propOnPageChange(targetPage);
    } else if (table) {
      table.setPageIndex(targetPage - 1);
    }
  };

  // Page size change handler
  const handlePageSizeChange = (newSize) => {
    if (newSize === size) return;

    if (propOnPageSizeChange) {
      propOnPageSizeChange(newSize);
    } else if (table) {
      if (typeof table.setPagination === "function") {
        table.setPagination((old) => ({
          ...old,
          pageIndex: 0,
          pageSize: newSize,
        }));
      } else {
        table.setPageSize(newSize);
        table.setPageIndex(0);
      }
    }
  };

  // Handle Quick Jumper submit
  const handleJumpSubmit = (e) => {
    e.preventDefault();
    const pageNum = parseInt(jumpInput, 10);
    if (isNaN(pageNum)) return;

    if (isTotalPagesKnown) {
      if (pageNum >= 1 && pageNum <= safeTotalPages) {
        handlePageChange(pageNum);
        setJumpInput("");
      } else {
        toast.warning(
          `Please enter a valid page between 1 and ${safeTotalPages}`,
        );
      }
    } else {
      if (pageNum >= 1) {
        handlePageChange(pageNum);
        setJumpInput("");
      }
    }
  };

  // Available page size options (ensure current size is included)
  const options = useMemo(() => {
    if (size && !pageSizeOptions.includes(size)) {
      return [...pageSizeOptions, size].sort((a, b) => a - b);
    }
    return pageSizeOptions;
  }, [pageSizeOptions, size]);

  // Generate numbered pages with smart ellipses
  const pageItems = useMemo(() => {
    if (!showPageNumbers || !isTotalPagesKnown || safeTotalPages <= 1) {
      return [];
    }

    // If total pages is 5 or less, show all page numbers without ellipsis
    if (safeTotalPages <= 5) {
      return Array.from({ length: safeTotalPages }, (_, i) => ({
        type: "page",
        value: i + 1,
      }));
    }

    const leftSiblingIndex = Math.max(current - siblingCount, 1);
    const rightSiblingIndex = Math.min(current + siblingCount, safeTotalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < safeTotalPages - 1;

    // Case 1: Start pages (e.g. 1 2 3 ... 71)
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftCount = Math.max(3, current + siblingCount);
      const items = Array.from({ length: leftCount }, (_, i) => ({
        type: "page",
        value: i + 1,
      }));
      items.push({ type: "ellipsis", direction: "right" });
      items.push({ type: "page", value: safeTotalPages });
      return items;
    }

    // Case 2: End pages (e.g. 1 ... 69 70 71)
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightCount = Math.max(
        3,
        safeTotalPages - current + 1 + siblingCount,
      );
      const startPage = safeTotalPages - rightCount + 1;
      const items = [{ type: "page", value: 1 }];
      items.push({ type: "ellipsis", direction: "left" });
      for (let i = startPage; i <= safeTotalPages; i++) {
        items.push({ type: "page", value: i });
      }
      return items;
    }

    // Case 3: Middle pages (e.g. 1 ... 34 35 36 ... 71)
    if (shouldShowLeftDots && shouldShowRightDots) {
      const items = [{ type: "page", value: 1 }];
      items.push({ type: "ellipsis", direction: "left" });
      for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
        items.push({ type: "page", value: i });
      }
      items.push({ type: "ellipsis", direction: "right" });
      items.push({ type: "page", value: safeTotalPages });
      return items;
    }

    return [];
  }, [current, safeTotalPages, isTotalPagesKnown, siblingCount, showPageNumbers]);

  // Calculate range of items shown
  const fromItem =
    totalItems !== undefined
      ? totalItems === 0
        ? 0
        : (current - 1) * size + 1
      : null;
  const toItem =
    totalItems !== undefined
      ? Math.min(current * size, totalItems)
      : null;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 py-3 px-3 sm:px-4",
        className,
      )}
    >
      {/* Left side: Item count & Selection info */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground min-w-0">
        {footerInfo ? (
          <div className="flex items-center gap-2">{footerInfo}</div>
        ) : totalItems !== undefined ? (
          <div>
            Showing{" "}
            <span className="font-semibold text-foreground">
              {fromItem}–{toItem}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-foreground">
              {totalItems.toLocaleString()}
            </span>{" "}
            {itemLabel}
          </div>
        ) : null}

        {isSelectionEnabled && (
          <>
            {(footerInfo || totalItems !== undefined) && (
              <span className="text-muted-foreground/30 hidden sm:inline">|</span>
            )}
            <span>
              {selectedCount} of {filteredCount} row(s) selected
            </span>
          </>
        )}
      </div>

      {/* Right side: Page size, Numbers, Navigation, Jumper */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 ml-auto">
        {/* Rows per page dropdown */}
        {showPageSize && options.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="whitespace-nowrap hidden md:inline">Rows:</span>
            <Select
              value={String(size)}
              onValueChange={(val) => handlePageSizeChange(Number(val))}
            >
              <SelectTrigger className="h-8 w-[68px] text-xs">
                <SelectValue placeholder={String(size)} />
              </SelectTrigger>
              <SelectContent side="top">
                {options.map((opt) => (
                  <SelectItem
                    key={opt}
                    value={String(opt)}
                    className="text-xs"
                  >
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Page status text: Page X of Y */}
        <div className="text-xs text-muted-foreground whitespace-nowrap px-1">
          Page{" "}
          <span className="font-semibold text-foreground">{current}</span>
          {isTotalPagesKnown && (
            <>
              {" "}of{" "}
              <span className="font-semibold text-foreground">
                {safeTotalPages}
              </span>
            </>
          )}
        </div>

        {/* Navigation Buttons: First, Prev, Numbers, Next, Last */}
        <div className="flex items-center gap-1">
          {/* First page button */}
          {isTotalPagesKnown && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handlePageChange(1)}
              disabled={!canPrevious}
              type="button"
              title="First page"
              aria-label="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          )}

          {/* Previous page button */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 sm:px-2.5 text-xs gap-1"
            onClick={() => handlePageChange(current - 1)}
            disabled={!canPrevious}
            type="button"
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Prev</span>
          </Button>

          {/* Numbered page buttons (hidden on xs screens) */}
          {pageItems.length > 0 && (
            <div className="hidden sm:flex items-center gap-1">
              {pageItems.map((item) => {
                if (item.type === "ellipsis") {
                  const isLeft = item.direction === "left";
                  const jumpTarget = isLeft
                    ? Math.max(1, current - 5)
                    : Math.min(safeTotalPages, current + 5);

                  return (
                    <Button
                      key={`ellipsis-${item.direction}`}
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => handlePageChange(jumpTarget)}
                      title={
                        isLeft
                          ? "Jump 5 pages backward"
                          : "Jump 5 pages forward"
                      }
                      aria-label={
                        isLeft
                          ? "Jump 5 pages backward"
                          : "Jump 5 pages forward"
                      }
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground group relative"
                    >
                      <span className="group-hover:hidden">...</span>
                      {isLeft ? (
                        <ChevronsLeft className="h-4 w-4 hidden group-hover:block" />
                      ) : (
                        <ChevronsRight className="h-4 w-4 hidden group-hover:block" />
                      )}
                    </Button>
                  );
                }

                const isCurrent = item.value === current;
                return (
                  <Button
                    key={`page-${item.value}`}
                    variant={isCurrent ? "default" : "outline"}
                    size="sm"
                    type="button"
                    onClick={() => handlePageChange(item.value)}
                    aria-current={isCurrent ? "page" : undefined}
                    className={cn(
                      "h-8 min-w-[32px] px-2 text-xs",
                      isCurrent && "font-semibold pointer-events-none shadow-sm",
                    )}
                  >
                    {item.value}
                  </Button>
                );
              })}
            </div>
          )}

          {/* Next page button */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 sm:px-2.5 text-xs gap-1"
            onClick={() => handlePageChange(current + 1)}
            disabled={!canNext}
            type="button"
            title="Next page"
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page button */}
          {isTotalPagesKnown && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handlePageChange(safeTotalPages)}
              disabled={!canNext}
              type="button"
              title="Last page"
              aria-label="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Quick Jumper Form */}
        {showQuickJumper && isTotalPagesKnown && safeTotalPages > 1 && (
          <form
            onSubmit={handleJumpSubmit}
            className="flex items-center gap-1.5"
          >
            <span className="text-xs text-muted-foreground whitespace-nowrap hidden lg:inline">
              Go to:
            </span>
            <Input
              type="number"
              min={1}
              max={safeTotalPages}
              value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value)}
              placeholder={String(current)}
              className="h-8 w-14 px-1.5 text-center text-xs"
              aria-label="Go to page number"
            />
            <Button
              variant="outline"
              size="sm"
              type="submit"
              className="h-8 px-2.5 text-xs"
            >
              Go
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
